import { useState, useRef, useEffect } from "react";
import WeekViz from "../components/WeekViz";

export default function Time(){
    const [venue, setVenue] = useState("");
    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [showCamera, setShowCamera] = useState(false);
    const [uploading, setUploading] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const [coverUrl, setCoverUrl] = useState(null);

    const [videoReady, setVideoReady] = useState(false);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);

    const [data, setData] = useState([]);
    const [testMessage, setTestMessage] = useState('');
    const testCanvasRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const previewAnimationRef = useRef(null);

    const postCheckInCheckOut = async (venue, checkIn, checkOut, coverUrl) => {
        try {
            const response = await fetch("http://localhost:8000/api/create-a-page-checkin-checkout", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                venue,
                checkin: checkIn,
                checkout: checkOut,
                coverUrl
            }),
            });

            if (!response.ok) {
            const txt = await response.text().catch(()=>null);
            throw new Error(`Network response was not ok: ${response.status} ${txt || ''}`);
            }

            console.log("Check In/Out posted successfully");
        } catch (error) {
            console.error("Error posting Check In/Out:", error.message);
        }
    };

    // upload image dataURL to server which returns a public URL
    const uploadImage = async (dataUrl) => {
        try {
            setUploading(true);
            const res = await fetch("http://localhost:8000/api/upload-image", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: dataUrl }),
            });
            // handle non-JSON error responses (HTML error pages) gracefully
            const text = await res.text();
            setUploading(false);
            if (!res.ok) {
                // try to parse JSON error
                try {
                    const parsed = JSON.parse(text);
                    throw new Error(parsed.error || parsed.message || 'upload failed');
                } catch (e) {
                    // not JSON -> return the text for debugging
                    throw new Error(text || 'upload failed');
                }
            }
            // success: parse JSON
            try {
                const json = JSON.parse(text);
                return json.url;
            } catch (e) {
                throw new Error('Invalid JSON response from upload');
            }
        } catch (err) {
            setUploading(false);
            console.error('uploadImage error', err.message);
            throw err;
        }
    };

    const startCamera = async (deviceId = null) => {
        try {
            // If a specific deviceId is provided, request it. Otherwise prefer front-facing camera.
            const constraints = deviceId
                ? { video: { deviceId: { exact: deviceId } } }
                : { video: { facingMode: 'user' } };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            console.log('got stream', stream);
            try {
              stream.getTracks().forEach(t => console.log('track', t.kind, t.readyState, t.enabled, t.getSettings && t.getSettings()));
            } catch (e) {
              console.log('could not log track settings', e);
            }
            if (videoRef.current) {
                const video = videoRef.current;
                video.muted = true; // ensure autoplay works in some browsers
                video.playsInline = true;
                // attach helpful debug event handlers (assign to avoid duplicates on restart)
                video.onloadeddata = () => {
                    console.log('video loadeddata', video.videoWidth, video.videoHeight, 'readyState', video.readyState);
                    if (video.videoWidth && video.videoHeight) setVideoReady(true);
                };
                video.onplaying = () => {
                    console.log('video playing event', video.videoWidth, video.videoHeight);
                    setVideoReady(true);
                };
                video.onerror = (e) => {
                    console.error('video element error', e);
                };

                // Assign srcObject after handlers are attached to avoid missing early events
                video.srcObject = stream;

                // Try to play immediately (user gesture should allow autoplay). If it fails, still start preview loop.
                (async () => {
                    try {
                        await video.play();
                        console.log('Camera preview playing');
                        setVideoReady(true);
                    } catch (playErr) {
                        console.warn('video.play() failed', playErr);
                    } finally {
                        // start canvas preview loop regardless of play() outcome so we still show pixels
                        startPreview();
                    }
                })();
            }
            setShowCamera(true);
            // After we have permission/stream, enumerate available video input devices so user can pick
            try {
                const all = await navigator.mediaDevices.enumerateDevices();
                const cams = all.filter(d => d.kind === 'videoinput');
                setDevices(cams);
                // if no selectedDeviceId yet, set to first available
                if (!selectedDeviceId && cams.length) setSelectedDeviceId(cams[0].deviceId);
            } catch (e) {
                console.warn('enumerateDevices failed', e);
            }
        } catch (err) {
            console.error('startCamera failed', err.message);
            alert('Could not access camera');
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.srcObject = null;
        }
        setShowCamera(false);
        setVideoReady(false);
        // stop preview loop if running
        if (previewAnimationRef.current) {
            cancelAnimationFrame(previewAnimationRef.current);
            previewAnimationRef.current = null;
        }
    };

    const startPreview = () => {
        if (previewAnimationRef.current) return;
        const loop = async () => {
            const stream = streamRef.current;
            if (!stream) {
                previewAnimationRef.current = requestAnimationFrame(loop);
                return;
            }
            const track = stream.getVideoTracks()[0];
            const canvas = previewCanvasRef.current;
            if (!canvas) {
                previewAnimationRef.current = requestAnimationFrame(loop);
                return;
            }

            // Try ImageCapture if available (more reliable frame grab)
            if (track && window.ImageCapture) {
                try {
                    const ic = new ImageCapture(track);
                    const bitmap = await ic.grabFrame();
                    canvas.width = bitmap.width;
                    canvas.height = bitmap.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(bitmap, 0, 0);
                    previewAnimationRef.current = requestAnimationFrame(loop);
                    return;
                } catch (e) {
                    // fallthrough to video drawing fallback
                }
            }

            // Fallback: draw from hidden/visible video element
            const video = videoRef.current;
            if (video && video.videoWidth && video.videoHeight) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                try {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                } catch (e) {
                    // ignore drawing errors
                }
            }

            previewAnimationRef.current = requestAnimationFrame(loop);
        };

        previewAnimationRef.current = requestAnimationFrame(loop);
    };

    // Try to grab a single frame from the active stream to verify the camera is delivering frames.
    const testStream = async () => {
        setTestMessage('Testing stream...');
        try {
            const stream = streamRef.current;
            if (!stream) throw new Error('No active stream. Start camera first.');

            const track = stream.getVideoTracks()[0];
            if (!track) throw new Error('No video track found on stream');

            // Prefer ImageCapture if available
            if (window.ImageCapture) {
                try {
                    const ic = new ImageCapture(track);
                    const bitmap = await ic.grabFrame();
                    const canvas = testCanvasRef.current;
                    canvas.width = bitmap.width;
                    canvas.height = bitmap.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(bitmap, 0, 0);
                    setTestMessage(`grabFrame OK: ${bitmap.width}x${bitmap.height}`);
                    return;
                } catch (e) {
                    console.warn('ImageCapture.grabFrame failed', e);
                    // fallthrough to video fallback
                }
            }

            // Fallback: attach stream to an offscreen video element and draw first frame
            await new Promise((resolve, reject) => {
                const v = document.createElement('video');
                v.playsInline = true;
                v.muted = true;
                v.srcObject = stream;
                const cleanup = () => {
                    v.pause();
                    v.srcObject = null;
                };
                let timeout = setTimeout(() => {
                    cleanup();
                    reject(new Error('timeout waiting for video frames'));
                }, 2000);
                v.onloadeddata = () => {
                    clearTimeout(timeout);
                    try {
                        const canvas = testCanvasRef.current;
                        canvas.width = v.videoWidth || 320;
                        canvas.height = v.videoHeight || 240;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
                        cleanup();
                        resolve();
                    } catch (e) {
                        cleanup();
                        reject(e);
                    }
                };
                v.onerror = (e) => {
                    clearTimeout(timeout);
                    cleanup();
                    reject(new Error('video element error'));
                };
                // attach to DOM briefly so some browsers allow autoplay
                v.style.position = 'fixed';
                v.style.left = '-9999px';
                document.body.appendChild(v);
                // try to play
                v.play().catch((err) => {
                    // ignore play errors; onloadeddata may still fire
                    console.warn('play() failed in test fallback', err);
                });
            });

            setTestMessage('Frame grabbed (fallback)');
        } catch (err) {
            console.error('testStream error', err);
            setTestMessage('Test failed: ' + (err.message || err));
        }
    };

    // take a photo, upload it and set coverUrl but do NOT post the checkin yet
    const takePhoto = async () => {
        // Prefer the preview canvas as the source because the <video> element preview can be black
        const previewCanvas = previewCanvasRef.current;
        const captureCanvas = canvasRef.current || document.createElement('canvas');
        if (previewCanvas && previewCanvas.width && previewCanvas.height) {
            // copy preview canvas into capture canvas (to control encoding size if needed)
            captureCanvas.width = previewCanvas.width;
            captureCanvas.height = previewCanvas.height;
            const ctx = captureCanvas.getContext('2d');
            ctx.drawImage(previewCanvas, 0, 0);
        } else {
            if (!videoRef.current) return;
            const video = videoRef.current;
            const w = video.videoWidth || 640;
            const h = video.videoHeight || 480;
            captureCanvas.width = w;
            captureCanvas.height = h;
            const ctx = captureCanvas.getContext('2d');
            ctx.drawImage(video, 0, 0, w, h);
        }
        const dataUrl = (captureCanvas).toDataURL('image/png');

        try {
            const url = await uploadImage(dataUrl);
            // set the cover URL so the user can confirm before submitting
            setCoverUrl(url);
            
            console.log(url);
            alert('Photo uploaded. Press Check In to post with this photo.');
        } catch (err) {
            alert('Upload failed: ' + (err.message || err));
        }
    };

    const fetchData = async () => {
        try {
            const response = await fetch("http://localhost:8000/api/query-a-database-checkin-checkout", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            });

            if (!response.ok) {
            const txt = await response.text().catch(()=>null);
            throw new Error(`Network response was not ok: ${response.status} ${txt || ''}`);
            }

            const result = await response.json();
            setData(result);

            console.log("data!!!!!!!!!", result);
        } catch (error) {
            console.error("Error posting Check In/Out:", error.message);
        }
    }

    useEffect(() => {
        fetchData();

        return () => {
            // cleanup camera on unmount
            stopCamera();
        };
    }, []);

    const handleSubmit = () => {
        if(!venue || !checkIn){ alert("write fields"); return;}

        // include the uploaded coverUrl (if any) so backend can attach it to the Notion page
        postCheckInCheckOut(venue, checkIn, checkOut, coverUrl);
    }

    return(
        <div className = "page">
            <WeekViz pWidth = { 40 } pFontSize = "var(--font-size-large)" pColor = "var(--black80)"/>

            {data.length}

            { data.map( (item, index) => (
                <div key = { index }>
                    <p>{ item.properties.venue.rich_text[0]?.plain_text }</p>
                    <p>{ item.properties.checkin.rich_text[0]?.plain_text }</p>
                    <p>{ item.properties.checkout.rich_text[0]?.plain_text }</p>
                    {/* <img src = { item.properties.image_url.rich_text[0]?.plain_text } alt="Check In Image" /> */}
                </div>
            ))}

            <h2>Check In</h2>

            <input
                type="text"
                placeholder="Venue"
                value = { venue }    
                onChange = { (e) => setVenue(e.target.value) }
            />

            <textarea
                type="text"
                placeholder="Check In"
                value = { checkIn }
                onChange = { (e) => setCheckIn(e.target.value) }
            />

            <button onClick = { handleSubmit }>Check In</button>
            {' '}

            <button onClick={() => startCamera(selectedDeviceId)}>Start Camera</button>
            <button onClick={stopCamera} disabled={!showCamera}>Stop Camera</button>

            <div style={{ display: 'inline-block', marginLeft: 12 }}>
                {devices.length ? (
                    <>
                        <label style={{ marginRight: 8 }}>Camera:</label>
                        <select
                            value={selectedDeviceId || ''}
                            onChange={(e) => {
                                const id = e.target.value || null;
                                setSelectedDeviceId(id);
                                // if camera is active, restart with new device
                                if (showCamera) {
                                    stopCamera();
                                    // small delay to ensure tracks are stopped
                                    setTimeout(() => startCamera(id), 150);
                                }
                            }}
                        >
                            {devices.map(d => (
                                <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>
                            ))}
                        </select>
                        <button style={{ marginLeft: 8 }} onClick={async () => {
                            try {
                                const all = await navigator.mediaDevices.enumerateDevices();
                                const cams = all.filter(d => d.kind === 'videoinput');
                                setDevices(cams);
                                if (!selectedDeviceId && cams.length) setSelectedDeviceId(cams[0].deviceId);
                            } catch (e) {
                                console.warn('enumerateDevices failed', e);
                            }
                        }}>Refresh cameras</button>
                    </>
                ) : (
                    <button onClick={async () => {
                        try {
                            const all = await navigator.mediaDevices.enumerateDevices();
                            const cams = all.filter(d => d.kind === 'videoinput');
                            setDevices(cams);
                            if (cams.length) setSelectedDeviceId(cams[0].deviceId);
                        } catch (e) {
                            console.warn('enumerateDevices failed', e);
                            alert('Could not list devices. Grant camera permission and try Start Camera first.');
                        }
                    }}>Detect cameras</button>
                )}
            </div>
            <div style={{ display: 'inline-block', marginLeft: 12 }}>
                <button onClick={testStream} disabled={!streamRef.current}>Test Stream</button>
                <span style={{ marginLeft: 8 }}>{testMessage}</span>
                <div style={{ marginTop: 8 }}>
                    <canvas ref={testCanvasRef} style={{ maxWidth: 320, display: testMessage ? 'block' : 'none' }} />
                </div>
            </div>
                        {showCamera && (
                            <div style={{ marginTop: 8 }}>
                                    {/* visible video may be black in some environments; use canvas preview as fallback */}
                                    <video
                                        ref={videoRef}
                                        style={{ width: 320, height: 240, background: '#000', display: 'inline-block' }}
                                        autoPlay
                                        muted
                                        playsInline
                                    />
                                    <canvas ref={previewCanvasRef} style={{ width: 320, height: 240, marginLeft: 8, verticalAlign: 'top', border: '1px solid #ddd' }} />
                                        <div style={{ marginTop: 8 }}>
                                                <button onClick={takePhoto} disabled={uploading}>Upload Photo to S3</button>
                                                {uploading && <span style={{ marginLeft: 8 }}>Uploading...</span>}
                                                {coverUrl && (
                                                    <div style={{ marginTop: 8 }}>
                                                        <div>Preview:</div>
                                                        <img src={coverUrl} alt="preview" style={{ maxWidth: 320, marginTop: 4 }} />
                                                    </div>
                                                )}
                                        </div>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
            )}
            
            <br/><br/><br/>

            <h2>Check Out</h2>
            <textarea
                type="text"
                placeholder="Check Out"
                value = { checkOut }
                onChange = { (e) => setCheckOut(e.target.value) }
            />
            <button>Check Out</button>
        </div>
    )
}